import React, {useCallback, useContext, useEffect, useState} from "react"
import {
    getAuthHeader,
    getItems,
    getLocalStorageItem,
    loadChildrenGoogleFolders,
    objectLang,
    setLocalStorageItem
} from "../functions"
import {AuthContext} from "../context/AuthContext"
import config from "../config.json"
import {useHttp} from "../hooks/http.hook"
import {WindowPopup} from "../components/WindowPopup"
import {LinksClass} from "../components/LinksClass"

export const GoogleDriveUploadFilePage = () => {
    setLocalStorageItem("page", "GoogleDriveUploadFilePage")

    const auth = useContext(AuthContext)
    const { request } = useHttp()

    const [files, setFiles] = useState([])
    const [lang] = useState(objectLang().language)
    const [fileNames, setFileNames] = useState({})          // { originalName: currentName }
    const [fileProgress, setFileProgress] = useState({})    // { originalName: 0..100 }

    const [tree, setTree] = useState({})
    const [selectedFolder, setSelectedFolder] = useState({ id: "root", name: "Корневая папка" })

    const [uploading, setUploading] = useState(false)
    const [totalProgress, setTotalProgress] = useState(0)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)
    const [currentIndex, setCurrentIndex] = useState(0)


    const [dragActive, setDragActive] = useState(false)

    const host = window.location.host === "localhost:3000" ? config.localHost : config.onrender

    // --- DRAG ---
    const handleDrag = e => {
        e.preventDefault()
        e.stopPropagation()

        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true)
        } else if (e.type === "dragleave") {
            setDragActive(false)
        }
    }

    const handleDrop = e => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files.length) {
            const selected = Array.from(e.dataTransfer.files)
            setFiles(selected)
            setFileNames(
                Object.fromEntries(selected.map(f => [f.name, f.name]))
            )
            setFileProgress({})
            setResult(null)
            setError(null)
            setTotalProgress(0)
        }
    }

    // --- TREE ---
    const loadChildren = useCallback(async (parentId = "root") => {
        try {
            setTree(prev => ({
                ...prev,
                [parentId]: { ...prev[parentId], loading: true }
            }))

            const res = await request(
                `${host}/api/google-drive/content`,
                "POST",
                {
                    parentId,
                    pageToken: null,
                    type: "folders",
                    MAX_ITEMS: 1000,
                    sort: "name",
                    order: "asc",
                    URI: window.location.origin,
                    lang: lang.lang
                },
                {
                    Authorization: getAuthHeader(auth),
                    "Content-Type": "application/json"
                }
            )

            return loadChildrenGoogleFolders(res.documents, parentId, setTree)
        } catch {
            setTree(prev => ({
                ...prev,
                [parentId]: { ...prev[parentId], loading: false, parentId }
            }))
            return []
        }
    }, [request, auth, host, lang])

    useEffect(() => {

        const initTree = async () => {
            const currentFolder = getLocalStorageItem("google-drive-current-folder-tree") || { 0: "root" }
            const folderIds = Object.values(currentFolder)
            const lastFolderId = folderIds[folderIds.length - 1]
            setTree({
                root: {
                    id: "root",
                    name: lang.body.googleDrivePage.googleUploadFile.rootFolder,
                    children: [],
                    loaded: false,
                    expanded: true,
                    loading: false,
                    parentId: ""
                }
            })

            let parentId = "root"

            for (const folderId of folderIds.slice(1)) {
                if (getLocalStorageItem(parentId)) {
                    const localRes = getLocalStorageItem(parentId)
                    loadChildrenGoogleFolders(localRes, parentId, setTree)
                } else {
                    await loadChildren(parentId)
                }
                parentId = folderId
                setSelectedFolder(prev => ({ ...prev, id: folderId }))
            }
            if (getLocalStorageItem(lastFolderId)) {
                const localRes = getLocalStorageItem(lastFolderId)
                loadChildrenGoogleFolders(localRes, lastFolderId, setTree)
            } else {
                await loadChildren(lastFolderId)
            }
        }

        initTree().then()
    }, [loadChildren, lang])

    const getParentIds = (tree, node) => {
        const parents = []
        let current = node

        while (current.parentId) {
            const parent = tree[current.parentId]
            if (!parent) break

            parents.push(parent.id)
            current = parent
        }
        return parents
    }

    const handleToggle = async node => {
        setSelectedFolder({ id: node.id, name: node.name })

        if (!node.loaded) {
            if (getLocalStorageItem(node.id)) {
                const localRes = getLocalStorageItem(node.id)
                loadChildrenGoogleFolders(localRes, node.id, setTree)
            } else {
                await loadChildren(node.id)
            }
        }

        setTree(prev => {
            const parentIds = getParentIds(prev, node)
            const newTree = {}
            Object.values(prev).forEach(n => {
                newTree[n.id] = {
                    ...n,
                    expanded: n.id === node.id || parentIds.includes(n.id)
                }
            })
            return newTree
        })
    }

    // --- FILES ---
    const handleFileChange = e => {
        const selected = Array.from(e.target.files || [])
        setFiles(selected)
        setFileNames(
            Object.fromEntries(selected.map(f => [f.name, f.name]))
        )
        setResult(null)
        setError(null)
        setTotalProgress(0)
        setFileProgress({})
    }

    const recalcTotalProgress = (nextFileProgress, filesList) => {
        if (!filesList.length) {
            setTotalProgress(0)
            return
        }
        const sum = filesList.reduce((acc, f) => {
            const p = nextFileProgress[f.name] ?? 0
            return acc + p
        }, 0)
        const total = Math.round(sum / filesList.length)
        setTotalProgress(total)
    }

    const uploadSingleFile = file =>
        new Promise(resolve => {
            const originalName = file.name
            const finalName = fileNames[originalName] || originalName
            const renamedFile = new File([file], finalName, { type: file.type })

            const formData = new FormData()
            formData.append("fileName", encodeURIComponent(finalName))
            formData.append("file", renamedFile)
            formData.append("URI", window.location.origin)
            formData.append("parentId", selectedFolder.id)

            const xhr = new XMLHttpRequest()
            xhr.open("POST", `${host}/api/google-drive/upload`, true)
            xhr.withCredentials = true
            xhr.setRequestHeader("Authorization", getAuthHeader(auth))

            // 0–78% — клиентская загрузка
            xhr.upload.onprogress = event => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 78)
                    setFileProgress(prev => {
                        const next = { ...prev, [originalName]: percent }
                        recalcTotalProgress(next, files)
                        return next
                    })
                }
            }

            xhr.onload = async () => {
                if (xhr.status < 200 || xhr.status >= 300) {
                    setError(`Ошибка при отправке файла: ${finalName}`)
                    setFileProgress(prev => {
                        const next = { ...prev, [originalName]: 0 }
                        recalcTotalProgress(next, files)
                        return next
                    })
                    return resolve()
                }

                const { uploadId } = JSON.parse(xhr.responseText)
                if (!uploadId) {
                    setError("Не удалось получить uploadId")
                    setFileProgress(prev => {
                        const next = { ...prev, [originalName]: 0 }
                        recalcTotalProgress(next, files)
                        return next
                    })
                    return resolve()
                }

                // 78–100% — прогресс с сервера
                const pollInterval = 250
                const poll = setInterval(async () => {
                    try {
                        const statusRes = await fetch(`${host}/api/google-drive/upload/status/${uploadId}`, {
                            headers: { "Authorization": getAuthHeader(auth) },
                            credentials: "include"
                        })
                        const { progress: serverProgress } = await statusRes.json()

                        if (serverProgress === -1) {
                            clearInterval(poll)
                            setError(`Ошибка при загрузке файла: ${finalName}`)
                            setFileProgress(prev => {
                                const next = { ...prev, [originalName]: 0 }
                                recalcTotalProgress(next, files)
                                return next
                            })
                            return resolve()
                        }

                        const totalForFile = 78 + Math.floor((serverProgress / 100) * 22)

                        setFileProgress(prev => {
                            const next = { ...prev, [originalName]: totalForFile }
                            recalcTotalProgress(next, files)
                            return next
                        })

                        if (serverProgress >= 100) {
                            clearInterval(poll)
                            resolve()
                        }
                    } catch {
                        clearInterval(poll)
                        setError("Ошибка сети при проверке статуса")
                        setFileProgress(prev => {
                            const next = { ...prev, [originalName]: 0 }
                            recalcTotalProgress(next, files)
                            return next
                        })
                        resolve()
                    }
                }, pollInterval)
            }

            xhr.onerror = () => {
                setError("Ошибка сети")
                setFileProgress(prev => {
                    const next = { ...prev, [originalName]: 0 }
                    recalcTotalProgress(next, files)
                    return next
                })
                resolve()
            }

            xhr.send(formData)
        })

    const handleUpload = async () => {
        if (!files.length) return setError("Файлы не выбраны")

        setUploading(true)
        setError(null)
        setResult(null)
        setTotalProgress(0)
        setFileProgress({})

        for (let i = 0; i < files.length; i++) {
            setCurrentIndex(i)
            await uploadSingleFile(files[i])
        }

        setUploading(false)
        setResult({ ok: true })
    }

    return (
        <>
            <LinksClass
                auth={auth}
                documents={getLocalStorageItem('documents')}
                items={getItems(getLocalStorageItem('documents'))}
                rootFolder={auth.rootFolder}
            />
            <WindowPopup
                data={{
                    tree,
                    selectedFolder,
                    uploading,
                    progress: totalProgress,
                    result,
                    error,
                    files,
                    fileNames,
                    setFileNames,
                    fileProgress,
                    handleToggle,
                    handleFileChange,
                    handleUpload,
                    handleDrag,
                    handleDrop,
                    dragActive,
                    currentIndex,
                    typeData: 'google-upload-file'
                }}
                lang={lang}
                title={lang.body.googleDrivePage.googleUploadFile.title}
            />
        </>
    )
}
